import { AddCircleOutline, Delete, Edit, PermMedia } from "@mui/icons-material";
import { Box, Grid, IconButton, Tooltip, Typography } from "@mui/material";
import { useRef } from "react";
import Dropzone from "../Dropzone/Dropzone";

type BrandImageUploadProps<FileType> = {
  variant?: "favicon" | "shortLogo" | "longLogo";
  hideCopyButton?: boolean | undefined;
  buttonAlignment?: "right" | "left";
  handleDrop?: (e: React.DragEvent<Element>) => void;
  file: FileType;
  handleFileChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  accept?: string;
  multiple?: boolean;
  text?: string;
  onRemove?: (file: FileType) => void;
  onCopyIcon?: () => void;
  disabled?: boolean;
  size?: string | number;
};

const BrandImageUpload = <FileType,>({
  buttonAlignment,
  hideCopyButton,
  variant,
  handleDrop,
  file,
  handleFileChange,
  multiple,
  onRemove,
  accept,
  text,
  disabled,
  onCopyIcon,
  size = "180px",
}: BrandImageUploadProps<FileType>) => {
  const inputRef = useRef<HTMLInputElement | null>(null);

  const display = {
    width: "0%",
    height: "0%",
    background: "",
    position: {
      left: "0%",
      top: "0%",
    },
  };

  if (variant == "favicon") {
    display.width = "13%";
    display.height = "13%";
    display.background = "/assets/images/brand_upload/FAVICON.png";
    display.position.left = "71.1%";
    display.position.top = "26.9%";
  }
  if (variant == "shortLogo") {
    display.width = "11.5%";
    display.height = "11.5%";
    display.background = "/assets/images/brand_upload/SHORT_LOGO.png";
    display.position.left = "5.2%";
    display.position.top = "55.4%";
  }
  if (variant == "longLogo") {
    display.width = "65%";
    display.height = "25%";
    display.background = "/assets/images/brand_upload/LONG_LOGO.png";
    display.position.left = "27.8%";
    display.position.top = "33.3%";
  }

  return (
    <Grid
      sx={{
        width: size,
        height: size,
        aspectRatio: "1/1",
        borderRadius: 10,
        boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
        border: "1px solid rgba(0, 0, 0, 0.2)",
        overflow: "hidden",
        transition: "box-shadow 0.3s ease, transform 0.3s ease",
        "&:hover": {
          boxShadow: "0px 6px 15px rgba(0, 0, 0, 0.2)",
          transform: "scale(1.02)",
        },
      }}
    >
      <Dropzone
        sx={{
          height: "100%",
          width: "100%",
          background: `URL("${display.background}")`,
          backgroundSize: "contain",
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          overflow: "hidden",
          borderRadius: "12px", // Match the container's rounded corners
        }}
        variant={disabled ? "subtle" : "obvious"}
        onDrop={(e) => {
          if (disabled) {
            return false;
          }
          handleDrop?.(e);
        }}
      >
        <Box
          sx={{
            height: "100%",
            width: "100%",
            position: "relative",
            "&:hover .upload-controls": {
              opacity: 1,
            },
          }}
        >
          <Box
            height={"100%"}
            width={"100%"}
            sx={{
              position: "relative",
            }}
          >
            {file && (
              <Box
                sx={{
                  position: "absolute",
                  left: display.position.left,
                  top: display.position.top,
                  width: display.width,
                  height: display.height,
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "hidden",
                    borderRadius: "25%",
                  }}
                >
                  <img
                    src={file as string}
                    alt="favicon"
                    style={{
                      maxWidth: "100%",
                      maxHeight: "100%",
                      objectFit: "contain",
                    }}
                  />
                </Box>
              </Box>
            )}
            <Box
              className="upload-controls"
              sx={{
                position: "absolute",
                bottom: 0,
                width: "100%",
                display: "flex",
                flexDirection:
                  buttonAlignment === "right" ? "row-reverse" : "row",
                opacity: 0,
                transition: "opacity 0.3s ease",
                boxShadow:
                  "inset 0px -20px 20px -10px rgba(255, 255, 255, 0.8)",
                px: 1,
              }}
            >
              {variant == "longLogo" && onCopyIcon && !hideCopyButton && (
                <Tooltip title="Use Short Logo">
                  <IconButton onClick={onCopyIcon}>
                    <PermMedia />
                  </IconButton>
                </Tooltip>
              )}
              {file && (
                <Tooltip title="Remove Logo">
                  <IconButton
                    onClick={() => {
                      if (onRemove) onRemove(file);
                    }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              )}
              <Tooltip title="Update Logo">
                <IconButton
                  onClick={() => {
                    if (inputRef && inputRef.current) {
                      inputRef.current?.click();
                    }
                  }}
                >
                  {file ? <Edit /> : <AddCircleOutline />}
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Box>

        <input
          id="fileToUpload"
          ref={inputRef}
          accept={accept ?? "image/*"}
          multiple={multiple}
          hidden
          type="file"
          onChange={handleFileChange}
        />
        {text && (
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", color: "gray" }}
          >
            {text}
          </Typography>
        )}
      </Dropzone>
    </Grid>
  );
};

export default BrandImageUpload;
